import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import { useCallback, useEffect, useRef } from 'react'

// Fallback when requestAnimationFrame does not fire (background tab)
const maxDelay = 250

/**
 * GTM dispatch that pushes to the dataLayer after the next paint.
 *
 * GTM runs all matching tags synchronously inside dataLayer.push, so a push in a click handler
 * delays the next frame by hundreds of ms on mobile (INP). Events keep their order, and the queue
 * is flushed synchronously on pagehide so nothing is lost when the page unloads.
 */
export const useDeferredGTMDispatch = (): ((data: unknown) => void) => {
  const dispatch = useGTMDispatch()
  const queue = useRef<unknown[]>([])
  const scheduled = useRef<boolean>(false)

  const flush = useCallback(() => {
    scheduled.current = false
    queue.current.splice(0).forEach((data) => dispatch(data))
  }, [dispatch])

  useEffect(() => {
    window.addEventListener('pagehide', flush)
    return () => window.removeEventListener('pagehide', flush)
  }, [flush])

  return useCallback(
    (data: unknown) => {
      queue.current.push(data)
      if (scheduled.current) {
        return
      }
      scheduled.current = true
      const fallbackTimeout = setTimeout(flush, maxDelay)
      requestAnimationFrame(() =>
        setTimeout(() => {
          clearTimeout(fallbackTimeout)
          flush()
        }, 0)
      )
    },
    [flush]
  )
}
